// main.ts - Main entry point for the application
export interface Package {
  setup(): void;
  // [key: string]: any;
}

type Route = string | string[] | ((currentRoute: string) => boolean);

interface PackagePlugin {
  name: string;
  loader: () => Promise<{
    default: new () => Package;
  }>;
  route?: Route;
}

export class PackageManager {
  private packages: PackagePlugin[] = [];
  private loadedPackages = new Set<string>();
  private currentRoute: string;

  constructor() {
    this.currentRoute = this.getCurrentRoute();
  }

  /**
   * Get current route from window location
   */
  private getCurrentRoute(): string {
    return (window as any).__CURRENT_ROUTE__;
  }

  /**
   * Register a package with optional route matching
   */
  register(packageConfig: PackagePlugin): void {
    this.packages.push(packageConfig);
  }

  /**
   * Check if a route matches the current route
   */
  private matchesRoute(route: Route): boolean {
    if (typeof route === 'string') {
      return this.currentRoute === route;
    }

    if (typeof route === 'object' && Array.isArray(route)) {
      return route.includes(this.currentRoute);
    }

    if (typeof route === 'function') {
      return route(this.currentRoute);
    }

    return false;
  }

  /**
   * Load a specific package
   */
  private async loadPackage(pkg: PackagePlugin): Promise<void> {
    if (this.loadedPackages.has(pkg.name)) {
      return;
    }

    try {
      const module = await pkg.loader();

      if (!module.default || typeof module.default !== 'function') {
        throw new Error(
          `Package ${pkg.name} must export a default class that implements Package interface`
        );
      }

      // Create package instance
      const PackageClass = module.default;
      const packageInstance = new PackageClass();

      // Check if instance has setup method
      if (typeof packageInstance.setup !== 'function') {
        throw new Error(`Package ${pkg.name} must implement setup() method`);
      }

      await packageInstance.setup();

      this.loadedPackages.add(pkg.name);
    } catch (error) {
      console.error(`Failed to load package ${pkg.name}:`, error);
    }
  }

  /**
   * Load packages that match current route
   */
  private async loadRoutePackages(): Promise<void> {
    const routePackages = this.packages.filter((pkg) => !pkg.route || this.matchesRoute(pkg.route));

    const loadPromises = routePackages.map((pkg) => this.loadPackage(pkg));
    await Promise.all(loadPromises);
  }

  /**
   * Initialize the package manager
   */
  async init(): Promise<void> {
    await this.loadRoutePackages();
  }

  /**
   * Get list of loaded packages
   */
  getLoadedPackages(): string[] {
    return Array.from(this.loadedPackages);
  }
}

// Create global package manager instance
const packageManager = new PackageManager();

// Export the register function for use in package definitions
export const registerPackage = (config: PackagePlugin) => {
  packageManager.register(config);
};

export const loadPackages = () => {
  return packageManager.init();
};
